/**
 * Escapes special regex characters in a string to make it safe for use in RegExp constructor
 * @param str - The string to escape
 * @returns The escaped string safe for regex use
 */
const escapeRegExp = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Formats a PascalCase field name to a user-friendly format
 * Adds spaces before capital letters (except the first one)
 *
 * Examples:
 * - "OnJobTime" → "On Job Time"
 * - "StartTime" → "Start Time"
 * - "JobType" → "Job Type"
 * - "CustomerId" → "Customer Id"
 * - "PONumber" → "PO Number"
 *
 * @param fieldName - The field name in PascalCase format
 * @returns Formatted field name with spaces
 */
export const formatFieldName = (fieldName: string): string => {
  if (!fieldName) return fieldName;

  return fieldName
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between lowercase and uppercase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Add space between consecutive capitals followed by lowercase
    .trim();
};

/**
 * Formats a validation error message to be more user-friendly
 * Replaces field names in PascalCase with formatted versions
 *
 * @param message - The error message from backend
 * @param fieldName - The field name (optional, will be extracted from message if not provided)
 * @returns Formatted error message
 */
export const formatValidationMessage = (message: string, fieldName?: string): string => {
  if (!message) return message;

  // If field name is provided, replace it in the message
  if (fieldName) {
    const formattedFieldName = formatFieldName(fieldName);
    // Escape regex special characters before creating RegExp
    const escapedFieldName = escapeRegExp(fieldName);
    // Replace the PascalCase field name with formatted version (case-insensitive)
    return message.replace(new RegExp(escapedFieldName, 'gi'), formattedFieldName);
  }

  // Otherwise, try to find and replace common PascalCase patterns in the message
  // Match patterns like "OnJobTime", "StartTime", etc.
  return message.replace(/\b([A-Z][a-z]+(?:[A-Z][a-z]+)+)\b/g, (match) => formatFieldName(match));
};

/**
 * Removes the field name from the beginning of an error message if it's already being displayed as a prefix.
 * This prevents duplication like "On Job Time: On Job Time cannot be less than Leave Time"
 *
 * @param errorMessage - The error message that may contain the field name at the start
 * @param fieldName - The formatted field name that will be displayed as a prefix
 * @returns The error message with the field name removed from the beginning if it was present
 *
 * @example
 * ```ts
 * removeFieldNameFromMessage("On Job Time cannot be less than Leave Time", "On Job Time")
 * // Returns: "cannot be less than Leave Time"
 * ```
 */
export const removeFieldNameFromMessage = (errorMessage: string, fieldName: string): string => {
  if (!errorMessage || !fieldName) return errorMessage;

  // Escape special regex characters in the field name
  const escapedFieldName = escapeRegExp(fieldName);
  // Match field name at start, optionally followed by colon and/or spaces
  const fieldNameRegex = new RegExp(`^${escapedFieldName}(:?\\s+|\\s+)`, 'i');

  if (fieldNameRegex.test(errorMessage)) {
    return errorMessage.replace(fieldNameRegex, '').trim();
  }

  return errorMessage;
};
